import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { MaskedLine, Reveal } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import ProductCard from "@/components/ProductCard";
import Stars from "@/components/Stars";

const HERO_IMAGE = "https://images.unsplash.com/photo-1618220179428-22790b461013?auto=format&fit=crop&w=1200&q=80";

const CHAPTERS = [
  {
    n: "01",
    title: "RAW MATERIALS",
    quote: "We begin where the earth ends — clay, hide, wax, brass.",
    text: "Every object starts as a negotiation with matter. We source stoneware from single pits, hides from one tannery, brass from a foundry that still pours by hand.",
  },
  {
    n: "02",
    title: "SCULPTURAL PURITY",
    quote: "Nothing is decorated. Everything is resolved.",
    text: "Our artisans remove until only the essential gesture remains. A curve that holds light. A seam that will outlive its maker.",
  },
  {
    n: "03",
    title: "PERMANENCE IN USE",
    quote: "An object should age like a friendship — deeper, quieter, better.",
    text: "We make things for decades, not seasons. Patina is not damage; it is the record of a life well shared.",
  },
];

function Hero() {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 700], [0, 130]);
  const scale = useTransform(scrollY, [0, 700], [1, 1.14]);

  return (
    <section className="spotlight-frame relative flex min-h-screen items-center overflow-hidden">
      <div className="pointer-events-none absolute -left-40 top-1/3 h-[500px] w-[500px] rounded-full bg-vermilion/[0.05] blur-[120px]" />
      <div className="grid w-full grid-cols-1 items-center gap-16 px-6 pb-24 pt-36 sm:px-12 lg:grid-cols-12 lg:px-20">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 1 }}
            className="eyebrow-label mb-8"
          >
            Atelier Norte — Est. MMXXVI
          </motion.p>
          <h1 className="font-display text-4xl font-light leading-[1.02] tracking-tight text-bone sm:text-5xl lg:text-6xl">
            <MaskedLine delay={0.35}>TACTILE RELICS</MaskedLine>
            <MaskedLine delay={0.5}>
              FOR THE <span className="italic text-vermilion">MINIMALIST</span>
            </MaskedLine>
            <MaskedLine delay={0.65}>RELIQUARY.</MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="mt-8 max-w-md text-base font-light leading-relaxed text-smoke md:text-lg"
          >
            Ceramics, leather, brass and flame — a small catalog of objects made slowly, meant to be kept forever.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/shop"
              data-testid="hero-explore-button"
              className="group flex items-center gap-3 bg-vermilion px-8 py-4 font-mono text-xs uppercase tracking-[0.3em] text-obsidian transition-colors hover:bg-vermilion-dark"
            >
              Explore Objects
              <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <a
              href="#manifesto"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById("manifesto")?.scrollIntoView({ behavior: "smooth" });
              }}
              data-testid="hero-manifesto-button"
              className="border border-white/20 px-8 py-4 font-mono text-xs uppercase tracking-[0.3em] text-smoke transition-colors hover:border-bone hover:text-bone"
            >
              The Manifesto
            </a>
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.5, duration: 1 }}
            className="mt-14 flex gap-10 font-mono text-[10px] uppercase tracking-[0.3em] text-faint"
          >
            <span>120+ Objects</span>
            <span>40 Artisans</span>
            <span>1 Atelier</span>
          </motion.div>
        </div>
        <motion.div
          initial={{ opacity: 0, x: 60 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.7, duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
          className="hidden lg:col-span-5 lg:block"
        >
          <motion.div style={{ y }} className="relative">
            <div className="border border-white/10 p-3">
              <div className="overflow-hidden">
                <motion.img
                  src={HERO_IMAGE}
                  alt="Atelier interior with curated objects"
                  style={{ scale }}
                  className="aspect-[3/4] w-full object-cover"
                />
              </div>
            </div>
            <div className="absolute -bottom-5 -left-5 border border-white/10 bg-obsidian px-4 py-3 font-mono text-[10px] uppercase tracking-[0.25em] text-smoke">
              The Atelier — Studio 4
            </div>
          </motion.div>
        </motion.div>
      </div>
      <motion.div
        animate={{ y: [0, 8, 0] }}
        transition={{ repeat: Infinity, duration: 2.4, ease: "easeInOut" }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-faint"
      >
        <ArrowDown size={16} strokeWidth={1} />
      </motion.div>
    </section>
  );
}

function FeaturedSpotlight({ product }) {
  if (!product) return null;
  return (
    <section className="px-6 py-24 sm:px-12 lg:px-20 lg:py-36">
      <div className="grid items-center gap-12 lg:grid-cols-12">
        <Reveal className="lg:col-span-6">
          <div className="group relative overflow-hidden border border-white/10">
            <img
              src={product.image}
              alt={product.name}
              className="aspect-[4/3] w-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-105"
            />
            <div className="pointer-events-none absolute inset-0 shadow-[inset_0_0_120px_rgba(0,0,0,0.6)]" />
            <span className="absolute left-4 top-4 bg-vermilion px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-obsidian">
              Object of the Season
            </span>
          </div>
        </Reveal>
        <div className="lg:col-span-5 lg:col-start-8">
          <Reveal>
            <p className="eyebrow-label mb-6">Spotlight — No. 001</p>
            <h2 className="font-display text-3xl font-light leading-tight text-bone sm:text-4xl lg:text-5xl">{product.name}</h2>
            <p className="mt-6 text-base font-light leading-relaxed text-smoke md:text-lg">{product.description}</p>
            <p className="mt-6 font-mono text-xs uppercase tracking-[0.25em] text-faint">{product.materials}</p>
            <Link
              to={`/product/${product.id}`}
              data-testid="spotlight-view-button"
              className="mt-10 inline-flex items-center gap-3 border-b border-vermilion pb-2 font-mono text-xs uppercase tracking-[0.3em] text-bone transition-colors hover:text-vermilion"
            >
              Examine the Object <ArrowRight size={14} />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function Manifesto() {
  return (
    <section id="manifesto" className="border-t border-white/10 px-6 py-24 sm:px-12 lg:px-20 lg:py-36">
      <Reveal>
        <p className="eyebrow-label mb-4">The Manifesto</p>
        <h2 className="mb-20 max-w-2xl font-display text-3xl font-light leading-tight text-bone sm:text-4xl lg:text-5xl">
          Three chapters on why we make things that refuse to be replaced.
        </h2>
      </Reveal>
      <div className="flex flex-col gap-24">
        {CHAPTERS.map((c, i) => (
          <Reveal key={c.n} delay={0.05}>
            <div className={`grid gap-8 lg:grid-cols-12 ${i % 2 === 1 ? "" : ""}`}>
              <div className={`lg:col-span-3 ${i % 2 === 1 ? "lg:order-2 lg:col-start-10" : ""}`}>
                <span className="font-mono text-7xl font-light text-vermilion/25 lg:text-8xl">{c.n}</span>
                <p className="mt-2 font-mono text-xs uppercase tracking-[0.35em] text-bone">{c.title}</p>
              </div>
              <div className={`lg:col-span-8 ${i % 2 === 1 ? "lg:order-1 lg:col-start-1" : "lg:col-start-5"}`}>
                <p className="font-display text-2xl font-light italic leading-snug text-bone sm:text-3xl">“{c.quote}”</p>
                <p className="mt-6 max-w-xl text-base font-light leading-relaxed text-smoke">{c.text}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function ReviewsSection({ reviews }) {
  if (!reviews.length) return null;
  return (
    <section className="border-t border-white/10 px-6 py-24 sm:px-12 lg:px-20 lg:py-36">
      <Reveal>
        <p className="eyebrow-label mb-4">Correspondence from Keepers</p>
        <h2 className="mb-16 font-display text-3xl font-light text-bone sm:text-4xl lg:text-5xl">Words that found their way back.</h2>
      </Reveal>
      <div className="grid gap-6 md:grid-cols-3">
        {reviews.slice(0, 3).map((r, i) => (
          <Reveal key={r.id} delay={i * 0.1}>
            <div className="flex h-full flex-col border border-white/10 bg-onyx p-8" data-testid={`review-card-${r.id}`}>
              <Stars rating={r.rating} />
              <p className="mt-6 flex-1 font-display text-xl font-light italic leading-relaxed text-bone">“{r.text}”</p>
              <div className="mt-8 flex items-center justify-between">
                <span className="font-mono text-xs uppercase tracking-[0.25em] text-smoke">{r.name}</span>
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-brass">Verified Keeper</span>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

export default function Home() {
  const [products, setProducts] = useState([]);
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    api.get("/products").then(({ data }) => setProducts(data)).catch(() => {});
    api.get("/reviews").then(({ data }) => setReviews(data)).catch(() => {});
  }, []);

  const featured = products.filter((p) => p.featured);
  const spotlight = featured[0];

  return (
    <div data-testid="home-page">
      <Hero />
      <Marquee />
      <FeaturedSpotlight product={spotlight} />
      <section className="px-6 pb-24 sm:px-12 lg:px-20 lg:pb-36">
        <Reveal className="mb-14 flex items-end justify-between">
          <div>
            <p className="eyebrow-label mb-4">The Catalog — Selection</p>
            <h2 className="font-display text-3xl font-light text-bone sm:text-4xl lg:text-5xl">Currently in the Atelier</h2>
          </div>
          <Link to="/shop" data-testid="view-all-button" className="hidden items-center gap-2 font-mono text-xs uppercase tracking-[0.3em] text-smoke transition-colors hover:text-vermilion sm:flex">
            View All <ArrowRight size={14} />
          </Link>
        </Reveal>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4 lg:gap-8">
          {(featured.length ? featured : products).slice(0, 4).map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
        </div>
      </section>
      <Manifesto />
      <ReviewsSection reviews={reviews} />
    </div>
  );
}
